import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '@/lib/api';
import { useAuthStore, type AuthUser, type OrganisationSummary, type OrgSummary } from '@/stores/auth';

interface ProfileResponse {
  user: AuthUser;
  orgs: OrgSummary[];
  organisations?: OrganisationSummary[];
}

/**
 * Who am I, and what may I do?
 *
 * The server is the authority: the persisted store is only a hint that lets the
 * shell render immediately. Whatever comes back here overwrites it, so a
 * permission an admin revoked five minutes ago disappears on the next load
 * rather than lingering until the user signs out.
 */
export function useProfile() {
  const setSession = useAuthStore((s) => s.setSession);

  const query = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.get<ProfileResponse>('/auth/me'),
    // A 401 means the session is gone; api.ts has already redirected to /login,
    // and retrying would just do it again.
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (query.data) setSession(query.data.user, query.data.orgs, query.data.organisations);
  }, [query.data, setSession]);

  return query;
}
